// The gift card page (sections/gift-card.liquid): the chosen amount and finish (black, silver, gold) on the card and the
// amount on the button; the recipient fields only while "Send it to someone else" is ticked (disabled fields aren't
// sent, so Shopify emails the buyer instead); the visitor's time-zone offset for the send date, which Shopify allows up
// to 90 days ahead. And the card itself: a ticket that rocks slowly in 3D, after the owner's video of adidas' Golden
// Ticket at about a quarter of its pace, with the light moving over the surface, the embossing and the glossy print.
import { motion } from 'fx';
import { addToCart } from 'cart';

const view = document.querySelector('[data-gc]');
if (view) init(view, initTicket(view));

function day(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function init(view, ticket) {
  const onCard = [...view.querySelectorAll('.gt [data-gc-amount]')];
  const elsewhere = [...view.querySelectorAll('[data-gc-amount]')].filter((el) => !el.closest('.gt'));
  // The amount on the card shrinks to fit: 1 000 kr is twice as long as €50.
  const fit = () => onCard.forEach((el) => {
    el.style.fontSize = '';
    const max = Number(el.dataset.gtFit) || 1000;
    const w = el.getComputedTextLength();
    if (w > max) el.style.fontSize = `${((parseFloat(getComputedStyle(el).fontSize) * max) / w).toFixed(1)}px`;
  });
  const show = (picked) => {
    onCard.forEach((el) => { el.textContent = picked.dataset.amount; });
    fit();
    if (ticket) ticket.finish(picked.dataset.finish);
  };
  if (ticket) ticket.onReady = fit;
  view.addEventListener('change', (e) => {
    const picked = e.target.closest('.sizes') && view.querySelector('.sizes input:checked');
    if (!picked) return;
    elsewhere.forEach((el) => { el.textContent = picked.dataset.amount; });
    if (ticket) ticket.turn(() => show(picked));
    else show(picked);
  });
  const first = view.querySelector('.sizes input:checked');
  if (first) show(first);
  if (document.fonts) document.fonts.ready.then(fit);

  const offset = view.querySelector('[data-gc-offset]');
  if (offset) offset.value = String(new Date().getTimezoneOffset());
  const date = view.querySelector('#gc-date');
  if (date) {
    const d = new Date();
    date.min = day(d);
    d.setDate(d.getDate() + 90);
    date.max = day(d);
  }

  const send = view.querySelector('[data-gc-send]');
  const box = view.querySelector('[data-gc-recipient]');
  const toggle = () => {
    const on = !!(send && send.checked);
    if (!box) return;
    box.hidden = !on;
    box.querySelectorAll('input, textarea').forEach((f) => { f.disabled = !on; });
  };
  if (send) send.addEventListener('change', toggle);
  toggle();

  // Christmas Eve (shown in December): opens "Send it to someone else" with the date set, then asks for their email.
  const eve = view.querySelector('[data-gc-eve]');
  if (eve && send && date) {
    const mark = () => eve.setAttribute('aria-pressed', String(send.checked && date.value === eve.dataset.gcEve));
    eve.addEventListener('click', () => {
      send.checked = true;
      toggle();
      date.value = eve.dataset.gcEve;
      mark();
      const email = view.querySelector('#gc-email');
      if (email && !email.value) email.focus();
    });
    date.addEventListener('change', mark);
    send.addEventListener('change', mark);
  }

  // Add to cart in place, as everywhere in the shop, and show it in the cart drawer, since checkout usually comes next.
  const form = view.querySelector('form.gc-form');
  const add = form && form.querySelector('button.add');
  const note = form && form.querySelector('.p-buy .note');
  if (form && add) {
    const said = note ? note.textContent : '';
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      add.disabled = true;
      try {
        await addToCart(new FormData(form));
        if (note) note.textContent = said;
      } catch (err) {
        if (note) note.textContent = err.message || view.dataset.gcError || '';
      } finally {
        add.disabled = false;
      }
    });
  }
}

// The card. A pose is where the light falls on it (x, y: 0..1) and how far it's turned (ry, rx: degrees). By itself it
// rocks about its upright axis like the video (from 25° one way to 9° the other, once every 4.2 seconds) while the
// light sweeps across; a mouse over it takes over (it turns towards the pointer, the pointer is the light), and so does
// the phone's tilt (iPhone asks once, on the first tap on the card). Leaving hands back to the rock gently. With Motion
// off (the switch on the start page) it stands still, and it doesn't run while
// off screen. A new amount turns the card over, and the finish and amount change while its face is away.
// Two skins paint the pose: the SVG (CSS 3D, at once) and the real 3D card (gift-3d.js, three.js), which takes over once
// the page has loaded, in browsers with WebGL 2; if WebGL fails or its context is lost, the SVG comes back.
function initTicket(view) {
  const art = view.querySelector('[data-gt]');
  const card = art && art.querySelector('[data-gt-card]');
  if (!card) return null;
  const q = (sel) => card.querySelector(sel);
  const gloss = q('[data-gt-gloss]');
  const metal = q('[data-gt-metal]');
  const light = q('[data-gt-light]');
  const high = q('[data-gt-high]');
  const low = q('[data-gt-low]');
  const rimHigh = q('[data-gt-rim-high]');
  const rimLow = q('[data-gt-rim-low]');
  if (!gloss || !metal || !light || !high || !low || !rimHigh || !rimLow) return null;
  // Motion as the site has it (fx.js): on until the visitor switches it off.
  const still = () => !motion;

  const REST = { x: 0.18, y: 0.32, ry: -10, rx: 5 }; // the streak clear of the crest and the amount
  const PERIOD = 4200;
  const rock = (t) => {
    const s = Math.sin((t / PERIOD) * 2 * Math.PI);
    return { x: 0.5 + 0.42 * s, y: 0.36, ry: -8 + 17 * s, rx: 5 };
  };
  const toward = (x, y) => ({ x, y, ry: (x - 0.5) * 28, rx: (0.5 - y) * 16 });
  const mix = (a, b, k) => ({ x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k, ry: a.ry + (b.ry - a.ry) * k, rx: a.rx + (b.rx - a.rx) * k });
  const clamp = (n) => Math.min(1, Math.max(0, n));
  const shift = (el, dx, dy) => el.setAttribute('transform', `translate(${dx.toFixed(2)} ${dy.toFixed(2)})`);

  // the SVG: spin (0..1) turns it edge-on and back from the other side, as an SVG has no back
  const flat = { turnMs: 760 };
  flat.draw = (p, spin = 0) => {
    const a = 180 * spin;
    const flip = a < 90 ? a : a - 180;
    card.style.setProperty('--ry', `${(p.ry + flip).toFixed(2)}deg`);
    card.style.setProperty('--rx', `${p.rx.toFixed(2)}deg`);
    // the glossy streak and the bright band of the brushed metal cross the card with the light
    const sx = -350 + p.x * 2300;
    gloss.setAttribute('x1', (sx - 350).toFixed(0));
    gloss.setAttribute('x2', (sx + 350).toFixed(0));
    const mx = 100 + p.x * 1400;
    metal.setAttribute('x1', (mx - 1100).toFixed(0));
    metal.setAttribute('x2', (mx + 1100).toFixed(0));
    light.setAttribute('cx', (p.x * 1600).toFixed(0));
    light.setAttribute('cy', (p.y * 1000).toFixed(0));
    // the light's direction across the card (in card units, from above by default): the embossed frame, bars and rule
    // catch it on that side and are dark on the other; the edge is lit where it faces the light
    const vx = (p.x - 0.5) * 7 - 0.8;
    const vy = (p.y - 0.5) * 7 - 2.4;
    shift(high, vx, vy);
    shift(low, -vx * 1.15, -vy * 1.15);
    shift(rimHigh, -vx, -vy);
    shift(rimLow, vx, vy);
  };

  let skin = flat;
  let spin = 0;
  const draw = (p) => skin.draw(p, spin);

  let pose = REST;
  let goal = null;
  let from = REST;
  let since = performance.now();
  let turning = null;
  let seen = true;
  let raf = 0;
  let last = 0;
  const frame = (t) => {
    raf = 0;
    const dt = last ? Math.min(64, t - last) : 16;
    last = t;
    if (goal) {
      pose = mix(pose, goal, 1 - Math.exp(-dt / 90));
    } else {
      const k = Math.min(1, (t - since) / 1400);
      pose = from ? mix(from, rock(t), k * k * (3 - 2 * k)) : rock(t);
      if (k >= 1) from = null;
    }
    if (turning) {
      const k = Math.min(1, (t - turning.t0) / skin.turnMs);
      spin = k < 0.5 ? 2 * k * k : 1 - 2 * (1 - k) * (1 - k);
      if (spin >= 0.5 && !turning.done) {
        turning.done = true;
        turning.swap();
      }
      if (k >= 1) {
        spin = 0;
        turning = turning.next ? { t0: t, swap: turning.next, done: false } : null;
      }
    }
    draw(pose);
    run();
  };
  const run = () => {
    if (!raf && seen && !still()) raf = requestAnimationFrame(frame);
  };
  const settle = () => {
    if (still()) {
      cancelAnimationFrame(raf);
      raf = 0;
      spin = 0;
      if (turning) {
        if (!turning.done) turning.swap();
        if (turning.next) turning.next();
        turning = null;
      }
      draw((pose = REST));
    } else {
      last = 0;
      run();
    }
  };

  art.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || still()) return;
    const r = art.getBoundingClientRect();
    goal = toward(clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height));
    run();
  });
  art.addEventListener('pointerleave', () => {
    if (!goal) return;
    goal = null;
    from = pose;
    since = performance.now();
  });

  const tilt = (e) => {
    if (e.gamma == null || still()) return;
    goal = toward(clamp(0.5 + e.gamma / 40), clamp(0.5 + (e.beta - 40) / 50));
    run();
  };
  if (matchMedia('(pointer: coarse)').matches && 'DeviceOrientationEvent' in window) {
    const D = window.DeviceOrientationEvent;
    if (typeof D.requestPermission === 'function') {
      art.addEventListener('click', () => {
        D.requestPermission().then((s) => { if (s === 'granted') addEventListener('deviceorientation', tilt); }).catch(() => {});
      }, { once: true });
    } else {
      addEventListener('deviceorientation', tilt);
    }
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      seen = entry.isIntersecting;
      if (seen) {
        last = 0;
        run();
      }
    }).observe(art);
  }
  document.addEventListener('motion:change', settle);
  draw(REST);
  settle();

  // the real 3D card, once the page has loaded
  const upgrade = async () => {
    if (!('WebGL2RenderingContext' in window)) return;
    try {
      const { mount } = await import('gift-3d');
      const real = await mount(art, card.querySelector('svg'), {
        onLost: () => {
          skin = flat;
          art.classList.remove('is-3d');
          draw(pose);
        },
      });
      if (!real) return;
      skin = Object.assign(real, { turnMs: 1150 });
      if (api.onReady) api.onReady();
      skin.refresh();
      draw(pose);
      art.classList.add('is-3d');
    } catch (e) {
      // the SVG card stays
    }
  };
  if (document.readyState === 'complete') upgrade();
  else addEventListener('load', upgrade, { once: true });

  const api = {
    finish(name) {
      if (name) card.dataset.finish = name;
      if (skin.refresh) skin.refresh();
    },
    turn(swap) {
      if (still() || !seen || !raf) {
        swap();
      } else if (turning && !turning.done) {
        turning.swap = swap;
      } else if (turning) {
        turning.next = swap;
      } else {
        turning = { t0: performance.now(), swap, done: false };
      }
    },
  };
  return api;
}
